/**
 * /d/<id> - the public deliverable / progression-report URL (T-057, T-064).
 *
 * PR-16 / Decision 5: every deliverable lives at app.yesplease.studio/d/<id>
 * as a structured, branded, shareable, permanent page. TECH-10: the id is an
 * unguessable UUID, the page is public-by-default, search indexing is
 * disabled (noindex, emitted by PublicArtifactView via useNoindexHead). No
 * directory listing - this is a single dynamic-segment route, there is no
 * /d/ index.
 *
 * This is a LEAN entry point (ARCH-005 §6, T-057 AC): it does NOT mount the
 * portal shell, the auth guard, or the marketing layout. The route component
 * is its own code-split chunk (the TanStack router plugin splits per route
 * file), and the renderer surface is additionally lazy-loaded so the route
 * entry ships almost nothing until an id resolves - "ships only the renderer
 * code needed for the resolved task type" (T-057 AC #1). The per-type
 * renderers are all in one registry chunk; the registry's switch picks the
 * one component for the resolved kind and the markdown fallback covers the 11
 * non-Phase-1 types + 'other'.
 *
 * Read path is the public_deliverables / public_progression_reports views
 * only (ARCH-005 public-view-only, ARCH-002 §5). No RLS bypass.
 */

import { Suspense, lazy } from 'react';
import { createFileRoute } from '@tanstack/react-router';

const PublicArtifactView = lazy(() =>
  import('@/components/portal/renderers/PublicArtifactView').then((m) => ({
    default: m.PublicArtifactView,
  })),
);

export const Route = createFileRoute('/d/$id')({
  component: PublicDeliverableRoute,
});

function PublicDeliverableRoute() {
  const { id } = Route.useParams();
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-[color:var(--color-white)]">
          <div className="mx-auto max-w-3xl px-5 py-12">
            <p className="text-sm text-[color:var(--color-text-mute)]">
              Loading...
            </p>
          </div>
        </div>
      }
    >
      <PublicArtifactView id={id} />
    </Suspense>
  );
}
